import { useApi } from "~/composables/useApi";
import type { Contact } from "@asap/shared/schemas/contact";
import type { BusinessLifecycleStage } from "@asap/shared/types/supabase.types.gen";
import type { SortingState } from "@tanstack/vue-table";

type ContactsFilters = {
  pageIndex: number;
  pageSize: number;
  search?: string;
  companyId?: string;
  businessUnitIds?: string[];
  status?: BusinessLifecycleStage;
  orders: { column: string; direction: "asc" | "desc" }[];
};

type ContactsState = {
  selectedFilters: ContactsFilters;
  loading: boolean;
  totalItems: number;
  showCreateModal: boolean;
  sorting: SortingState;
};

const contactsState = reactive<ContactsState>({
  selectedFilters: {
    pageSize: 20,
    pageIndex: 0,
    orders: [],
    search: undefined,
    companyId: undefined,
    businessUnitIds: undefined,
    status: undefined,
  },
  totalItems: 0,
  showCreateModal: false,
  loading: false,
  sorting: [{ id: "created_at", desc: true }],
});

export const useContacts = function () {
  const { invoke } = useApi();

  const convertSortingToOrders = (sorting: SortingState): { column: string; direction: "asc" | "desc" }[] => {
    return sorting.map((sort) => ({
      column: sort.id,
      direction: sort.desc ? "desc" : "asc",
    }));
  };

  const selectMultipleForTable = async function (
    filters: ContactsFilters
  ): Promise<{ data: Contact[]; count: number }> {
    contactsState.loading = true;
    const order = filters.orders.at(0);

    const response = await invoke({
      path: "contacts",
      method: "GET",
      query: {
        pageIndex: filters.pageIndex,
        pageSize: filters.pageSize,
        search: filters.search,
        orderColumn: order?.column,
        orderDirection: order?.direction,
        companyId: filters.companyId,
        businessUnitIds: filters.businessUnitIds,
        status: filters.status,
      },
    });

    contactsState.loading = false;

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return { data: response.data.items, count: response.data.totalItems };
  };

  return {
    contactsState,
    selectMultipleForTable,
    convertSortingToOrders,
  };
};

export const useContact = function () {
  const { invoke } = useApi();

  const getContact = async function (id: string) {
    const response = await invoke({
      path: "contact/:id",
      method: "GET",
      params: { id },
    });
    if (!response.success) {
      throw new Error(response.error.clientMessage || response.error.message);
    }
    return response.data;
  };

  return { getContact };
};
